import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  AppleAuthRequest,
  AppleAuthSetupRequiredResponse,
  AuthResponse,
  GoogleAuthRequest,
  GoogleAuthSetupRequiredResponse,
  LoginRequest,
  RegisterRequest,
  UserRole,
} from '@kangaroo/shared';
import axios from 'axios';

import {
  authenticateWithApple,
  authenticateWithGoogle,
  completeRegistration,
  forgotPassword,
  login,
  register,
} from '../features/auth';
import { lightTheme, PaddiqLogo } from '@kangaroo/ui';
import { useAuthStore } from '../store/authStore';
import {
  getAppleSignInClientId,
  getAppleSignInRedirectUri,
  getGoogleOAuthClientId,
} from '../lib/runtimeConfig';
import { readSessionScanContinuation } from '../lib/sessionScanDestination';
import { clearChatGptContinuation, readChatGptContinuation } from '../lib/chatGptConnection';
import { isPasswordRecoveryRoute } from './authRouteState';

type AuthMode = 'login' | 'register';

type GoogleCredentialResponse = {
  credential?: string;
};

type AppleCredentialResponse = {
  authorization?: {
    id_token?: string;
  };
  user?: {
    email?: string;
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
};

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (options: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              width?: number;
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
            },
          ) => void;
        };
      };
    };
    AppleID?: {
      auth?: {
        init: (options: {
          clientId: string;
          scope: string;
          redirectURI: string;
          usePopup: boolean;
        }) => void;
        signIn: () => Promise<AppleCredentialResponse>;
      };
    };
  }
}

let googleIdentityScriptPromise: Promise<void> | null = null;
let googleIdentityInitializedClientId: string | null = null;
let appleIdentityScriptPromise: Promise<void> | null = null;
let appleIdentityInitializedClientId: string | null = null;

function loadGoogleIdentityScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google sign-in is not available'));
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  googleIdentityScriptPromise ??= new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]',
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Could not load Google sign-in')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Could not load Google sign-in')), {
      once: true,
    });
    document.head.appendChild(script);
  });

  return googleIdentityScriptPromise;
}

function loadAppleIdentityScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Sign in with Apple is not available'));
  }

  if (window.AppleID?.auth) {
    return Promise.resolve();
  }

  appleIdentityScriptPromise ??= new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"]',
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Could not load Sign in with Apple')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src =
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Could not load Sign in with Apple')), {
      once: true,
    });
    document.head.appendChild(script);
  });

  return appleIdentityScriptPromise;
}

function isGoogleSetupRequired(
  response: AuthResponse | GoogleAuthSetupRequiredResponse,
): response is GoogleAuthSetupRequiredResponse {
  return 'setupRequired' in response && response.setupRequired;
}

function isAppleSetupRequired(
  response: AuthResponse | AppleAuthSetupRequiredResponse,
): response is AppleAuthSetupRequiredResponse {
  return 'setupRequired' in response && response.setupRequired;
}

function formatAppleName(user: AppleCredentialResponse['user']) {
  return (
    [user?.name?.firstName, user?.name?.lastName]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' ') || undefined
  );
}

interface RegisterFormState {
  email: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
  parentRegistrationMode: 'create' | 'join';
  teamName: string;
  teamCode: string;
  requestedTeamCode: string;
}

const USER_ROLE_OPTIONS: UserRole[] = [
  'TEAM_OWNER' as UserRole,
  'COACH' as UserRole,
  'PARENT' as UserRole,
  'DRIVER' as UserRole,
];

function getErrorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? 'Request failed';
  }

  return error instanceof Error ? error.message : 'Unknown error';
}

function getRetryAfterMs(error: unknown) {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const retryAfter = error.response?.headers?.['retry-after'];
  const retryAfterValue = Array.isArray(retryAfter) ? retryAfter[0] : retryAfter;
  if (!retryAfterValue) {
    return null;
  }

  const retryAfterSeconds = Number(retryAfterValue);
  if (Number.isFinite(retryAfterSeconds)) {
    return Math.max(0, retryAfterSeconds * 1000);
  }

  const retryAfterDateMs = Date.parse(retryAfterValue);
  return Number.isFinite(retryAfterDateMs) ? Math.max(0, retryAfterDateMs - Date.now()) : null;
}

function getRateLimitMessage(error: unknown) {
  if (!axios.isAxiosError(error) || error.response?.status !== 429) {
    return null;
  }

  const retryAfterMs = getRetryAfterMs(error);
  if (!retryAfterMs || retryAfterMs < 1000) {
    return 'Too many sign-in attempts. Please wait a moment and try again.';
  }

  const retryAfterSeconds = Math.ceil(retryAfterMs / 1000);
  return `Too many sign-in attempts. Please wait ${retryAfterSeconds} seconds and try again.`;
}

function createsTeamOnRegistration(
  role: UserRole,
  parentRegistrationMode: RegisterFormState['parentRegistrationMode'],
) {
  return (
    role === 'TEAM_OWNER' ||
    role === 'COACH' ||
    (role === 'PARENT' && parentRegistrationMode === 'create')
  );
}

function requestsTeamOnRegistration(
  role: UserRole,
  parentRegistrationMode: RegisterFormState['parentRegistrationMode'],
) {
  return role === 'DRIVER' || (role === 'PARENT' && parentRegistrationMode === 'join');
}

function formatRegisterRole(role: UserRole) {
  return role
    .toLowerCase()
    .split('_')
    .map((part) => part[0]!.toUpperCase() + part.slice(1))
    .join(' ');
}

export function AuthPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const googleOAuthClientId = getGoogleOAuthClientId();
  const appleSignInClientId = getAppleSignInClientId();
  const appleSignInRedirectUri = getAppleSignInRedirectUri();
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const googleButtonRenderKeyRef = useRef<string | null>(null);
  const googleCredentialHandlerRef = useRef<(credential: string | undefined) => void>(
    () => undefined,
  );
  const googleSubmitInFlightRef = useRef(false);
  const appleSubmitInFlightRef = useRef(false);
  const googleRateLimitedUntilRef = useRef(0);
  const setupToken = useMemo(
    () => new URLSearchParams(location.search).get('setupToken') ?? '',
    [location.search],
  );
  const passwordRecoveryRoute = useMemo(
    () => isPasswordRecoveryRoute(location.search),
    [location.search],
  );
  const sessionScanContinuation = useMemo(
    () => readSessionScanContinuation(window.sessionStorage),
    [location.pathname, location.search],
  );
  const isCompletingRegistration = Boolean(setupToken);
  const isSessionScanAuthentication = Boolean(sessionScanContinuation);
  const chatGptContinuation = useMemo(
    () => readChatGptContinuation(window.sessionStorage),
    [location.pathname, location.search],
  );
  const setSession = useAuthStore((state) => state.setSession);
  const [mode, setMode] = useState<AuthMode>(
    location.pathname === '/register' || setupToken ? 'register' : 'login',
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registerStep, setRegisterStep] = useState(0);
  const [loginForm, setLoginForm] = useState<LoginRequest>({ email: '', password: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(passwordRecoveryRoute);
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);
  const [completeForm, setCompleteForm] = useState({ password: '', confirmPassword: '' });
  const [googleReady, setGoogleReady] = useState(false);
  const [appleReady, setAppleReady] = useState(false);
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState<string | null>(null);
  const [pendingAppleCredential, setPendingAppleCredential] = useState<{
    idToken: string;
    email?: string | null;
    name?: string | null;
  } | null>(null);
  const [registerForm, setRegisterForm] = useState<RegisterFormState>({
    email: '',
    password: '',
    role: 'TEAM_OWNER' as UserRole,
    parentRegistrationMode: 'create',
    confirmPassword: '',
    teamName: '',
    teamCode: '',
    requestedTeamCode: '',
  });
  const showTeamNameField =
    mode === 'register' &&
    createsTeamOnRegistration(registerForm.role, registerForm.parentRegistrationMode);
  const showRequestedTeamCodeField =
    mode === 'register' &&
    requestsTeamOnRegistration(registerForm.role, registerForm.parentRegistrationMode);
  const registerSteps = useMemo(() => ['Account', 'Role'], []);

  useEffect(() => {
    if (isCompletingRegistration) return;
    if (passwordRecoveryRoute) {
      setMode('login');
      setShowForgotPassword(true);
      setError(null);
      return;
    }
    setShowForgotPassword(false);
    setForgotMessage(null);
  }, [isCompletingRegistration, passwordRecoveryRoute]);

  const title = useMemo(() => {
    if (isCompletingRegistration) return 'Finish your Paddiq account';
    return mode === 'login' ? 'Sign in to Paddiq' : 'Create your Paddiq account';
  }, [isCompletingRegistration, mode]);

  async function handleAuthSuccess(response: AuthResponse, redirectTo?: string) {
    await setSession(response.token, response.user);
    const connectionDestination = readChatGptContinuation(window.sessionStorage);
    const destination =
      redirectTo ??
      (location.pathname.startsWith('/k/')
        ? `${location.pathname}${location.search}`
        : (connectionDestination ?? readSessionScanContinuation(window.sessionStorage) ?? '/drivers'));
    if (destination === connectionDestination) clearChatGptContinuation(window.sessionStorage);
    navigate(destination, { replace: true });
  }

  function buildGoogleAuthPayload(idToken: string): GoogleAuthRequest | null {
    if (mode !== 'register') {
      return { idToken };
    }

    const willCreateTeam = createsTeamOnRegistration(
      registerForm.role,
      registerForm.parentRegistrationMode,
    );
    const requestedTeamCode =
      requestsTeamOnRegistration(registerForm.role, registerForm.parentRegistrationMode) &&
      registerForm.requestedTeamCode.trim()
        ? registerForm.requestedTeamCode.trim().toUpperCase()
        : undefined;

    if (willCreateTeam && !registerForm.teamName.trim()) {
      setError('Team name is required');
      return null;
    }

    if (willCreateTeam && !registerForm.teamCode.trim()) {
      setError('Team code is required');
      return null;
    }

    if (
      registerForm.role === 'PARENT' &&
      registerForm.parentRegistrationMode === 'join' &&
      !requestedTeamCode
    ) {
      setError('Team code is required');
      return null;
    }

    return {
      idToken,
      role: registerForm.role,
      teamName: willCreateTeam ? registerForm.teamName.trim() : undefined,
      teamCode: willCreateTeam ? registerForm.teamCode.trim().toUpperCase() : undefined,
      requestedTeamCode,
    };
  }

  function buildAppleAuthPayload(input: {
    idToken: string;
    email?: string | null;
    name?: string | null;
  }): AppleAuthRequest | null {
    if (mode !== 'register') {
      return input;
    }

    const willCreateTeam = createsTeamOnRegistration(
      registerForm.role,
      registerForm.parentRegistrationMode,
    );
    const requestedTeamCode =
      requestsTeamOnRegistration(registerForm.role, registerForm.parentRegistrationMode) &&
      registerForm.requestedTeamCode.trim()
        ? registerForm.requestedTeamCode.trim().toUpperCase()
        : undefined;

    if (willCreateTeam && !registerForm.teamName.trim()) {
      setError('Team name is required');
      return null;
    }

    if (willCreateTeam && !registerForm.teamCode.trim()) {
      setError('Team code is required');
      return null;
    }

    if (
      registerForm.role === 'PARENT' &&
      registerForm.parentRegistrationMode === 'join' &&
      !requestedTeamCode
    ) {
      setError('Team code is required');
      return null;
    }

    return {
      ...input,
      role: registerForm.role,
      teamName: willCreateTeam ? registerForm.teamName.trim() : undefined,
      teamCode: willCreateTeam ? registerForm.teamCode.trim().toUpperCase() : undefined,
      requestedTeamCode,
    };
  }

  async function submitGoogleCredential(credential: string) {
    if (googleSubmitInFlightRef.current) {
      return;
    }

    const rateLimitedForMs = googleRateLimitedUntilRef.current - Date.now();
    if (rateLimitedForMs > 0) {
      setError(
        `Too many sign-in attempts. Please wait ${Math.ceil(rateLimitedForMs / 1000)} seconds and try again.`,
      );
      return;
    }

    const payload = buildGoogleAuthPayload(credential);
    if (!payload) {
      return;
    }

    googleSubmitInFlightRef.current = true;
    setIsSubmitting(true);

    try {
      const response = await authenticateWithGoogle(payload);
      if (isGoogleSetupRequired(response)) {
        setPendingGoogleCredential(credential);
        setRegisterForm((current) => ({
          ...current,
          email: response.email,
          password: '',
          confirmPassword: '',
        }));
        setMode('register');
        setRegisterStep(1);
        navigate('/register', { replace: true });
        return;
      }

      if (payload.requestedTeamCode && !response.user.teamId) {
        window.sessionStorage.setItem('gridline:joinRequestSent', response.user.id);
        await setSession(response.token, response.user);
        navigate('/setup-team?requestSent=1', { replace: true, state: { joinRequestSent: true } });
        return;
      }

      await handleAuthSuccess(response);
    } catch (submitError) {
      const rateLimitMessage = getRateLimitMessage(submitError);
      if (rateLimitMessage) {
        googleRateLimitedUntilRef.current = Date.now() + (getRetryAfterMs(submitError) ?? 60_000);
        setError(rateLimitMessage);
      } else {
        setError(getErrorMessage(submitError));
      }
    } finally {
      googleSubmitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }

  async function submitAppleCredential(credential: {
    idToken: string;
    email?: string | null;
    name?: string | null;
  }) {
    if (appleSubmitInFlightRef.current) {
      return;
    }

    const payload = buildAppleAuthPayload(credential);
    if (!payload) {
      return;
    }

    appleSubmitInFlightRef.current = true;
    setIsSubmitting(true);

    try {
      const response = await authenticateWithApple(payload);
      if (isAppleSetupRequired(response)) {
        setPendingAppleCredential({
          idToken: credential.idToken,
          email: response.email,
          name: response.name,
        });
        setRegisterForm((current) => ({
          ...current,
          email: response.email,
          password: '',
          confirmPassword: '',
        }));
        setMode('register');
        setRegisterStep(1);
        navigate('/register', { replace: true });
        return;
      }

      if (payload.requestedTeamCode && !response.user.teamId) {
        window.sessionStorage.setItem('gridline:joinRequestSent', response.user.id);
        await setSession(response.token, response.user);
        navigate('/setup-team?requestSent=1', { replace: true, state: { joinRequestSent: true } });
        return;
      }

      setPendingAppleCredential(null);
      await handleAuthSuccess(response);
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      appleSubmitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }

  async function handleGoogleCredential(credential: string | undefined) {
    setError(null);

    if (!credential) {
      setError('Google sign-in did not return a credential.');
      return;
    }

    if (mode === 'register' && registerStep === 0) {
      setPendingGoogleCredential(credential);
      setRegisterStep(1);
      return;
    }

    await submitGoogleCredential(credential);
  }

  async function handleApplePress() {
    setError(null);

    if (!appleReady || !appleSignInClientId) {
      setError('Sign in with Apple is still loading. Try again in a moment.');
      return;
    }

    try {
      const response = await window.AppleID!.auth!.signIn();
      const idToken = response.authorization?.id_token;
      if (!idToken) {
        setError('Sign in with Apple did not return an identity token.');
        return;
      }

      const credential = {
        idToken,
        email: response.user?.email,
        name: formatAppleName(response.user),
      };

      if (mode === 'register' && registerStep === 0) {
        setPendingAppleCredential(credential);
        setRegisterForm((current) => ({
          ...current,
          email: credential.email ?? current.email,
        }));
        setRegisterStep(1);
        return;
      }

      await submitAppleCredential(credential);
    } catch (appleError) {
      if ((appleError as { error?: string }).error !== 'popup_closed_by_user') {
        setError(getErrorMessage(appleError));
      }
    }
  }

  useEffect(() => {
    googleCredentialHandlerRef.current = (credential) => void handleGoogleCredential(credential);
  });

  useEffect(() => {
    if (!googleOAuthClientId || isCompletingRegistration) {
      return;
    }

    let cancelled = false;
    loadGoogleIdentityScript()
      .then(() => {
        if (!cancelled) {
          setGoogleReady(true);
        }
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(
            loadError instanceof Error ? loadError.message : 'Could not load Google sign-in',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [googleOAuthClientId, isCompletingRegistration]);

  useEffect(() => {
    if (!appleSignInClientId || isCompletingRegistration) {
      return;
    }

    let cancelled = false;
    loadAppleIdentityScript()
      .then(() => {
        if (cancelled) {
          return;
        }

        if (appleIdentityInitializedClientId !== appleSignInClientId) {
          window.AppleID?.auth?.init({
            clientId: appleSignInClientId,
            scope: 'name email',
            redirectURI: appleSignInRedirectUri,
            usePopup: true,
          });
          appleIdentityInitializedClientId = appleSignInClientId;
        }

        setAppleReady(true);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(
            loadError instanceof Error ? loadError.message : 'Could not load Sign in with Apple',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [appleSignInClientId, appleSignInRedirectUri, isCompletingRegistration]);

  useEffect(() => {
    if (!googleReady || !googleOAuthClientId || isCompletingRegistration) {
      return;
    }

    if (googleIdentityInitializedClientId !== googleOAuthClientId) {
      window.google?.accounts?.id?.initialize({
        client_id: googleOAuthClientId,
        callback: (response) => googleCredentialHandlerRef.current(response.credential),
      });
      googleIdentityInitializedClientId = googleOAuthClientId;
    }
  }, [googleOAuthClientId, googleReady, isCompletingRegistration]);

  useEffect(() => {
    const container = googleButtonRef.current;
    if (!googleReady || !googleOAuthClientId || !container || isCompletingRegistration) {
      return;
    }

    const buttonText = mode === 'register' ? 'signup_with' : 'signin_with';
    const buttonWidth = Math.floor(container.getBoundingClientRect().width);
    const renderKey = `${googleOAuthClientId}:${buttonText}:${buttonWidth}`;
    if (googleButtonRenderKeyRef.current === renderKey && container.childElementCount > 0) {
      return;
    }

    container.replaceChildren();
    window.google?.accounts?.id?.renderButton(container, {
      theme: 'outline',
      size: 'large',
      width: buttonWidth,
      text: buttonText,
    });
    googleButtonRenderKeyRef.current = renderKey;
  }, [googleOAuthClientId, googleReady, isCompletingRegistration, mode]);

  const renderGoogleButton = (
    dividerLabel = 'or',
    dividerPosition: 'before' | 'after' = 'before',
  ) =>
    googleOAuthClientId && !isCompletingRegistration ? (
      <Stack spacing={1.5}>
        {dividerPosition === 'before' ? <Divider>{dividerLabel}</Divider> : null}
        <Box
          ref={googleButtonRef}
          aria-label={mode === 'register' ? 'Sign up with Google' : 'Sign in with Google'}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            minHeight: 44,
            opacity: isSubmitting ? 0.62 : 1,
            pointerEvents: isSubmitting ? 'none' : 'auto',
            '& > div': {
              width: '100%',
            },
            '& [role="button"]': {
              width: '100% !important',
              maxWidth: 'none !important',
            },
          }}
        />
        {dividerPosition === 'after' ? <Divider>{dividerLabel}</Divider> : null}
      </Stack>
    ) : null;

  const renderAppleButton = (
    dividerLabel = googleOAuthClientId ? undefined : 'or',
    dividerPosition: 'before' | 'after' = 'before',
  ) =>
    appleSignInClientId && !isCompletingRegistration ? (
      <Stack spacing={1.5} sx={{ width: '100%' }}>
        {dividerLabel && dividerPosition === 'before' ? <Divider>{dividerLabel}</Divider> : null}
        <Button
          type="button"
          variant="outlined"
          size="large"
          fullWidth
          onClick={() => void handleApplePress()}
          disabled={!appleReady || isSubmitting}
          sx={{
            minHeight: 40,
            borderRadius: '4px',
            border: '1px solid #DADCE0',
            color: '#3C4043',
            bgcolor: '#FFFFFF',
            fontSize: '0.875rem',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': { borderColor: '#B8BABE', bgcolor: '#F8F9FA' },
          }}
        >
          {mode === 'register' ? 'Sign up with Apple' : 'Sign in with Apple'}
        </Button>
        {dividerLabel && dividerPosition === 'after' ? <Divider>{dividerLabel}</Divider> : null}
      </Stack>
    ) : null;

  async function handleForgotPasswordSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setForgotMessage(null);
    setIsSubmitting(true);

    try {
      const result = await forgotPassword(forgotEmail.trim());
      setForgotMessage(result.message);
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleLoginSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await handleAuthSuccess(await login(loginForm));
    } catch (submitError) {
      setError(getRateLimitMessage(submitError) ?? getErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleRegisterSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (
      !pendingGoogleCredential &&
      !pendingAppleCredential &&
      registerForm.password !== registerForm.confirmPassword
    ) {
      setError('Passwords do not match');
      return;
    }

    if (
      createsTeamOnRegistration(registerForm.role, registerForm.parentRegistrationMode) &&
      !registerForm.teamName.trim()
    ) {
      setError('Team name is required');
      return;
    }

    if (
      createsTeamOnRegistration(registerForm.role, registerForm.parentRegistrationMode) &&
      !registerForm.teamCode.trim()
    ) {
      setError('Team code is required');
      return;
    }

    if (
      registerForm.role === 'PARENT' &&
      registerForm.parentRegistrationMode === 'join' &&
      !registerForm.requestedTeamCode.trim()
    ) {
      setError('Team code is required');
      return;
    }

    if (pendingGoogleCredential) {
      await submitGoogleCredential(pendingGoogleCredential);
      return;
    }

    if (pendingAppleCredential) {
      await submitAppleCredential(pendingAppleCredential);
      return;
    }

    setIsSubmitting(true);

    try {
      const willCreateTeam = createsTeamOnRegistration(
        registerForm.role,
        registerForm.parentRegistrationMode,
      );
      const requestedTeamCode =
        requestsTeamOnRegistration(registerForm.role, registerForm.parentRegistrationMode) &&
        registerForm.requestedTeamCode.trim()
          ? registerForm.requestedTeamCode.trim().toUpperCase()
          : undefined;
      const payload: RegisterRequest = {
        email: registerForm.email.trim(),
        password: registerForm.password,
        name: registerForm.email.trim().split('@')[0] || 'Paddiq User',
        role: registerForm.role,
        teamName: willCreateTeam ? registerForm.teamName.trim() : undefined,
        teamCode: willCreateTeam ? registerForm.teamCode.trim().toUpperCase() : undefined,
        requestedTeamCode,
      };

      const response = await register(payload);
      if (requestedTeamCode && !response.user.teamId) {
        window.sessionStorage.setItem('gridline:joinRequestSent', response.user.id);
        await setSession(response.token, response.user);
        navigate('/setup-team?requestSent=1', { replace: true, state: { joinRequestSent: true } });
        return;
      }

      await handleAuthSuccess(response);
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleRegisterNext() {
    setError(null);

    if (!registerForm.email.trim() || !registerForm.password || !registerForm.confirmPassword) {
      setError('Email and password are required');
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setRegisterStep(1);
  }

  async function handleCompleteRegistrationSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (completeForm.password !== completeForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      await handleAuthSuccess(
        await completeRegistration({ setupToken, password: completeForm.password }),
      );
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ThemeProvider theme={lightTheme}>
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(360px, 0.9fr) minmax(520px, 1.1fr)' },
        bgcolor: '#FFFFFF',
      }}
    >
      <Box
        component="section"
        data-testid="auth-brand-panel"
        sx={{
          position: 'relative',
          display: { xs: 'none', md: 'flex' },
          minHeight: '100vh',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflow: 'hidden',
          px: { md: 6, lg: 9 },
          py: { md: 7, lg: 9 },
          bgcolor: '#F6F1FF',
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        <PaddiqLogo variant="header" wordmarkColor="#111111" wordmarkSize="1.45rem" />

        <Box sx={{ maxWidth: 500 }}>
          <Typography sx={{ mb: 2.5, color: 'primary.main', fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Your racing workspace
          </Typography>
          <Typography component="h1" sx={{ color: '#111111', fontSize: { md: '3.2rem', lg: '4rem' }, fontWeight: 800, letterSpacing: '-0.055em', lineHeight: 1.02 }}>
            Race data,
            <Box component="span" sx={{ display: 'block', color: 'primary.main' }}>
              without the headache.
            </Box>
          </Typography>
          <Typography sx={{ mt: 3, maxWidth: 430, color: 'text.secondary', fontSize: '1.05rem', lineHeight: 1.65 }}>
            Bring sessions, drivers, karts, video, and coaching into one place built for race day.
          </Typography>
        </Box>

        <Typography sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
          Built for drivers, teams, and coaches.
        </Typography>
      </Box>

      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflowY: 'auto',
          color: '#111111',
          px: { xs: 3, sm: 7, lg: 12 },
          py: 6,
        }}
      >
        <Stack spacing={2.5} sx={{ width: '100%', maxWidth: 460, my: 'auto' }}>
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
            <PaddiqLogo variant="header" wordmarkColor="#111111" wordmarkSize="1.4rem" />
          </Box>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h4" fontWeight={800}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Driver development and team operations in one place.
              </Typography>
            </Box>

            {chatGptContinuation && !isCompletingRegistration ? (
              <Alert severity="info">
                Sign in to review your app connection request. You will choose whether to allow access after signing in.
              </Alert>
            ) : null}

            {isSessionScanAuthentication && !chatGptContinuation && !isCompletingRegistration ? (
              <Alert severity="info" data-testid="session-scan-auth-guidance">
                <Typography variant="subtitle2" fontWeight={800}>
                  Open this race session in the full Paddiq app
                </Typography>
                <Typography variant="body2">
                  Sign in if you already use Paddiq. If you are new, choose Create account. We will
                  keep this scan and return you to this exact session after your account is ready
                  and has access.
                </Typography>
              </Alert>
            ) : null}

            {!isCompletingRegistration && (
              <Tabs
                value={mode}
                onChange={(_event, nextMode: AuthMode) => {
                  setMode(nextMode);
                  setError(null);
                  setRegisterStep(0);
                  setPendingGoogleCredential(null);
                  setPendingAppleCredential(null);
                  navigate(nextMode === 'login' ? '/login' : '/register', { replace: true });
                }}
              >
                <Tab label={isSessionScanAuthentication ? 'Sign in' : 'Login'} value="login" />
                <Tab
                  label={isSessionScanAuthentication ? 'Create account' : 'Register'}
                  value="register"
                />
              </Tabs>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {isCompletingRegistration ? (
              <Box component="form" onSubmit={handleCompleteRegistrationSubmit}>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Set your password to activate this account, then you will be signed in.
                  </Typography>
                  <TextField
                    label="Password"
                    type="password"
                    required
                    helperText="At least 8 characters"
                    value={completeForm.password}
                    onChange={(event) =>
                      setCompleteForm((current) => ({ ...current, password: event.target.value }))
                    }
                  />
                  <TextField
                    label="Confirm password"
                    type="password"
                    required
                    value={completeForm.confirmPassword}
                    onChange={(event) =>
                      setCompleteForm((current) => ({
                        ...current,
                        confirmPassword: event.target.value,
                      }))
                    }
                  />
                  <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                    {isSubmitting ? 'Finishing setup...' : 'Finish registration'}
                  </Button>
                </Stack>
              </Box>
            ) : mode === 'login' ? (
              showForgotPassword ? (
                <Box component="form" onSubmit={handleForgotPasswordSubmit}>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      Enter your account email and we will send a reset link if an account exists.
                    </Typography>
                    <TextField
                      label="Email"
                      type="email"
                      required
                      value={forgotEmail}
                      onChange={(event) => setForgotEmail(event.target.value)}
                    />
                    {forgotMessage ? <Alert severity="success">{forgotMessage}</Alert> : null}
                    <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending…' : 'Send reset link'}
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotMessage(null);
                        setError(null);
                      }}
                    >
                      Back to sign in
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleLoginSubmit}>
                  <Stack spacing={2}>
                    <TextField
                      label="Email or username"
                      required
                      value={loginForm.email}
                      onChange={(event) =>
                        setLoginForm((current) => ({ ...current, email: event.target.value }))
                      }
                    />
                    <TextField
                      label="Password"
                      type="password"
                      required
                      value={loginForm.password}
                      onChange={(event) =>
                        setLoginForm((current) => ({ ...current, password: event.target.value }))
                      }
                    />
                    <Button
                      type="button"
                      size="small"
                      sx={{ alignSelf: 'flex-end' }}
                      onClick={() => {
                        setShowForgotPassword(true);
                        setForgotEmail(loginForm.email.includes('@') ? loginForm.email : '');
                        setError(null);
                      }}
                    >
                      Forgot password?
                    </Button>
                    <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                      {isSubmitting ? 'Signing in...' : 'Sign in'}
                    </Button>
                    {renderGoogleButton()}
                    {renderAppleButton()}
                  </Stack>
                </Box>
              )
            ) : (
              <Box component="form" onSubmit={handleRegisterSubmit}>
                <Stack spacing={2}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={800}>
                        {registerSteps[registerStep]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        {registerStep + 1} of {registerSteps.length}
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        height: 3,
                        borderRadius: 999,
                        bgcolor: 'rgba(255,255,255,0.08)',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${((registerStep + 1) / registerSteps.length) * 100}%`,
                          height: '100%',
                          borderRadius: 'inherit',
                          bgcolor: 'primary.main',
                          transition: 'width 180ms ease',
                        }}
                      />
                    </Box>
                  </Stack>

                  {registerStep === 0 ? (
                    <>
                      <TextField
                        label="Email"
                        type="email"
                        required
                        value={registerForm.email}
                        onChange={(event) =>
                          setRegisterForm((current) => ({ ...current, email: event.target.value }))
                        }
                      />
                      <TextField
                        label="Password"
                        type="password"
                        required
                        helperText="At least 8 characters"
                        value={registerForm.password}
                        onChange={(event) =>
                          setRegisterForm((current) => ({
                            ...current,
                            password: event.target.value,
                          }))
                        }
                      />
                      <TextField
                        label="Confirm password"
                        type="password"
                        required
                        value={registerForm.confirmPassword}
                        onChange={(event) =>
                          setRegisterForm((current) => ({
                            ...current,
                            confirmPassword: event.target.value,
                          }))
                        }
                      />
                      <Button
                        type="button"
                        variant="contained"
                        size="large"
                        onClick={handleRegisterNext}
                      >
                        Continue
                      </Button>
                      {renderGoogleButton('or sign up with')}
                      {renderAppleButton('or sign up with')}
                    </>
                  ) : (
                    <Stack spacing={2}>
                      {isSessionScanAuthentication ? (
                        <Alert severity="info">
                          Choose the role that matches you. If you are joining an existing team, ask
                          its owner or the event organizer for the team code. Team approval may be
                          required before this scanned session can open; the scan will stay saved.
                        </Alert>
                      ) : null}
                      <TextField
                        label="Role"
                        select
                        value={registerForm.role}
                        onChange={(event) =>
                          setRegisterForm((current) => ({
                            ...current,
                            role: event.target.value as UserRole,
                          }))
                        }
                      >
                        {USER_ROLE_OPTIONS.map((role) => (
                          <MenuItem key={role} value={role}>
                            {formatRegisterRole(role)}
                          </MenuItem>
                        ))}
                      </TextField>

                      {registerForm.role === 'PARENT' && (
                        <Stack direction="row" spacing={1}>
                          <Button
                            type="button"
                            variant={
                              registerForm.parentRegistrationMode === 'create'
                                ? 'contained'
                                : 'outlined'
                            }
                            fullWidth
                            onClick={() =>
                              setRegisterForm((current) => ({
                                ...current,
                                parentRegistrationMode: 'create',
                              }))
                            }
                          >
                            Family Team
                          </Button>
                          <Button
                            type="button"
                            variant={
                              registerForm.parentRegistrationMode === 'join'
                                ? 'contained'
                                : 'outlined'
                            }
                            fullWidth
                            onClick={() =>
                              setRegisterForm((current) => ({
                                ...current,
                                parentRegistrationMode: 'join',
                              }))
                            }
                          >
                            Team Code
                          </Button>
                        </Stack>
                      )}

                      {showTeamNameField && (
                        <>
                          <TextField
                            label="Team name"
                            required
                            value={registerForm.teamName}
                            onChange={(event) =>
                              setRegisterForm((current) => ({
                                ...current,
                                teamName: event.target.value,
                              }))
                            }
                            helperText="This creates a family team workspace for your account."
                          />
                          <TextField
                            label="Team code"
                            required
                            value={registerForm.teamCode}
                            onChange={(event) =>
                              setRegisterForm((current) => ({
                                ...current,
                                teamCode: event.target.value.toUpperCase(),
                              }))
                            }
                            helperText="Set once. Drivers and parents use this code to request access."
                          />
                        </>
                      )}

                      {showRequestedTeamCodeField && (
                        <TextField
                          label="Team code"
                          required={registerForm.role === 'PARENT'}
                          value={registerForm.requestedTeamCode}
                          onChange={(event) =>
                            setRegisterForm((current) => ({
                              ...current,
                              requestedTeamCode: event.target.value.toUpperCase(),
                            }))
                          }
                          helperText={
                            registerForm.role === 'PARENT'
                              ? 'Sends a request for your parent account only. Drivers request teams separately.'
                              : 'Optional. Sends a request for a team owner or coach to approve.'
                          }
                        />
                      )}
                    </Stack>
                  )}

                  {registerStep === 1 && (
                    <Stack spacing={2}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <Button
                          type="button"
                          variant="outlined"
                          size="large"
                          fullWidth
                          onClick={() => {
                            setPendingGoogleCredential(null);
                            setPendingAppleCredential(null);
                            setRegisterStep(0);
                          }}
                        >
                          Back
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={isSubmitting}
                        >
                          {isSubmitting
                            ? 'Creating account...'
                            : pendingGoogleCredential
                              ? 'Finish with Google'
                              : pendingAppleCredential
                                ? 'Finish with Apple'
                                : 'Create account'}
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
    </ThemeProvider>
  );
}
